import { useEffect, useState, useRef } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  LifeBuoy, Send, Loader2, AlertCircle, Zap, CheckCircle2,
  MessageCircle,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';

const STATUS_LABELS = {
  open: { label: 'Open', cls: 'bg-[#EFF6FF] border-[#2563FF]/40 text-[#3B82F6]' },
  in_progress: { label: 'In progress', cls: 'bg-yellow-950/40 border-yellow-500/40 text-yellow-300' },
  closed: { label: 'Closed', cls: 'bg-gray-900/40 border-gray-600/40 text-[#64748B]' },
};

function fmt(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString();
}

export default function TicketPortalPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);
  const threadRef = useRef(null);

  const fetchTicket = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/support/portal/${id}?token=${encodeURIComponent(token)}`,
      );
      setTicket(data.ticket);
      setError(null);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not load ticket');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!id || !token) {
      setError('Missing token. Please open this page from the link in your email.');
      setLoading(false);
      return;
    }
    fetchTicket();
  }, [id, token]); // eslint-disable-line

  useEffect(() => {
    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [ticket?.messages?.length]);

  const submitReply = async (e) => {
    e?.preventDefault();
    const content = reply.trim();
    if (!content || sending) return;
    setSending(true);
    try {
      const { data } = await axios.post(
        `${API}/support/portal/${id}/reply?token=${encodeURIComponent(token)}`,
        { content },
      );
      setTicket(data.ticket);
      setReply('');
      toast.success('Sent — the team will get back to you soon');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#2563FF]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white border border-[#E2E8F0] p-8 text-center">
          <AlertCircle className="w-12 h-12 text-[#2563FF] mx-auto mb-4" />
          <h1 className="font-heading text-xl text-[#0F172A] mb-2" data-testid="portal-error-title">
            Can't load this ticket
          </h1>
          <p className="font-body text-sm text-[#64748B] mb-6">{error}</p>
          <Link
            to="/support"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#2563FF] hover:bg-[#2563FF] text-[#0F172A] text-xs font-heading uppercase tracking-widest"
          >
            <LifeBuoy className="w-3.5 h-3.5" />
            Open a new ticket
          </Link>
        </div>
      </div>
    );
  }

  if (!ticket) return null;

  const statusInfo = STATUS_LABELS[ticket.status] || STATUS_LABELS.open;
  const isClosed = ticket.status === 'closed';

  return (
    <div className="min-h-screen bg-[#F5F7FB] text-[#0F172A]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-2 mb-3">
            <div className="w-7 h-7 bg-[#2563FF] flex items-center justify-center">
              <Zap className="w-4 h-4 text-[#0F172A]" />
            </div>
            <span className="font-heading text-xs uppercase tracking-widest text-[#0F172A]">
              CRATELAB Support
            </span>
          </div>
          <div className="flex items-start justify-between gap-4 mb-2">
            <h1
              className="font-heading text-2xl sm:text-3xl text-[#0F172A] tracking-tight"
              data-testid="portal-ticket-subject"
            >
              {ticket.subject}
            </h1>
            <span
              className={`shrink-0 text-[10px] font-mono uppercase tracking-wider px-2 py-1 border ${statusInfo.cls}`}
              data-testid="portal-ticket-status"
            >
              {statusInfo.label}
            </span>
          </div>
          <p className="font-mono text-[11px] text-[#64748B] mb-6">
            Ticket <span className="text-[#2563FF]">#{ticket.id?.slice(0, 8)}</span> ·
            opened {fmt(ticket.created_at)} · {ticket.category}
          </p>
        </motion.div>

        {/* Thread */}
        <div
          ref={threadRef}
          className="space-y-3 mb-6 max-h-[60vh] overflow-y-auto pr-1"
          data-testid="portal-thread"
        >
          {(ticket.messages || []).map((m, i) => {
            const mine = m.role === 'user';
            return (
              <motion.div
                key={m.id || i}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex ${mine ? 'justify-end' : 'justify-start'}`}
                data-testid={`portal-msg-${m.role}`}
              >
                <div className="max-w-[88%]">
                  <div
                    className={`px-4 py-3 text-sm whitespace-pre-wrap leading-relaxed ${
                      mine
                        ? 'gradient-primary text-white shadow-[0_4px_12px_rgba(37,99,255,0.35)]'
                        : 'bg-white border border-[#E2E8F0] text-[#0F172A]'
                    }`}
                  >
                    {m.content}
                  </div>
                  <p className={`font-mono text-[10px] text-[#94A3B8] mt-1 ${mine ? 'text-right' : ''}`}>
                    {mine ? (m.author || ticket.name || 'You') : 'CRATELAB Team'} · {fmt(m.created_at)}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Reply form / closed notice */}
        {isClosed ? (
          <div className="border border-gray-700/40 bg-gray-950/30 p-4 flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-[#64748B] shrink-0 mt-0.5" />
            <div>
              <p className="font-body text-sm text-[#0F172A]">This ticket is closed.</p>
              <p className="font-body text-xs text-[#64748B] mt-1">
                Need more help? <Link to="/support" className="text-[#2563FF] hover:text-[#3B82F6] underline">Open a new ticket</Link>.
              </p>
            </div>
          </div>
        ) : (
          <form
            onSubmit={submitReply}
            className="border border-[#E2E8F0] bg-white p-3"
            data-testid="portal-reply-form"
          >
            <textarea
              value={reply}
              onChange={(e) => setReply(e.target.value)}
              placeholder="Type your reply…"
              rows={3}
              className="w-full bg-transparent border-0 outline-none text-sm text-[#0F172A] placeholder-gray-600 resize-none"
              data-testid="portal-reply-input"
              maxLength={5000}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) submitReply(e);
              }}
            />
            <div className="flex items-center justify-between pt-2 border-t border-[#E2E8F0]">
              <p className="font-mono text-[10px] text-[#475569]">
                ⌘/Ctrl + Enter to send · {reply.length} / 5000
              </p>
              <button
                type="submit"
                disabled={!reply.trim() || sending}
                className="inline-flex items-center gap-1.5 px-4 py-1.5 bg-[#2563FF] hover:bg-[#2563FF] disabled:bg-[#EFF6FF] disabled:text-[#64748B] text-[#0F172A] text-xs font-heading uppercase tracking-widest"
                data-testid="portal-reply-send"
              >
                {sending ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Send className="w-3.5 h-3.5" />
                )}
                Send
              </button>
            </div>
          </form>
        )}

        <div className="flex items-center justify-between mt-8 pt-6 border-t border-[#E2E8F0]">
          <Link
            to="/support"
            className="font-mono text-[11px] text-[#64748B] hover:text-[#0F172A] uppercase tracking-wider flex items-center gap-1"
          >
            <MessageCircle className="w-3.5 h-3.5" />
            Open another ticket
          </Link>
          <span className="font-mono text-[10px] text-[#475569]">
            cratelab.io/support
          </span>
        </div>
      </div>
    </div>
  );
}
